/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\stores\menu.ts
 * 菜单状态管理
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
// 避免直接依赖 vue-router 命名空间类型，定义最简路由类型
type SimpleRoute = { path: string; meta?: Record<string, unknown> }
import type { MenuItem } from '../types'

export const useMenuStore = defineStore('menu', () => {
  // 状态
  const menuItems = ref<MenuItem[]>([])
  const breadcrumbs = ref<{ title: string; path?: string }[]>([])
  const activeMenuId = ref<string>('')
  const isLoading = ref(false)
  
  // 计算属性
  const flatMenuItems = computed(() => {
    const flatten = (items: MenuItem[]): MenuItem[] => {
      const result: MenuItem[] = []
      for (const item of items) {
        result.push(item)
        if (item.children) {
          result.push(...flatten(item.children))
        }
      }
      return result
    }
    return flatten(menuItems.value)
  })
  
  const findMenuById = computed(() => (id: string): MenuItem | undefined => {
    return flatMenuItems.value.find(item => item.id === id)
  })
  
  const findMenuByPath = computed(() => (path: string): MenuItem | undefined => {
    return flatMenuItems.value.find(item => item.path === path)
  })
  
  // 操作方法
  const setMenuItems = (items: MenuItem[]) => {
    menuItems.value = items
  }
  
  const setActiveMenu = (menuId: string) => {
    activeMenuId.value = menuId
    updateBreadcrumbsById(menuId)
    try { recordRecentlyVisitedById(menuId) } catch {}
  }
  
  const updateBreadcrumbs = (route: SimpleRoute) => {
    const crumbs: { title: string; path?: string }[] = []
    
    // 首先尝试从路由元信息获取面包屑
    if (route.meta?.breadcrumbs && Array.isArray(route.meta.breadcrumbs)) {
      // 使用路由 meta 中预定义的面包屑路径
      crumbs.push(...route.meta.breadcrumbs as { title: string; path?: string }[])
    } else {
      // 否则尝试基于菜单结构智能生成面包屑
      const currentPath = route.path
      const menuItem = findMenuByPath.value(currentPath)
      
      if (menuItem) {
        // 根据菜单项构建完整的层级路径
        const buildMenuPath = (targetItem: MenuItem, items: MenuItem[], path: MenuItem[] = []): MenuItem[] => {
          for (const item of items) {
            const currentPath = [...path, item]
            if (item.id === targetItem.id || item.path === targetItem.path) {
              return currentPath
            }
            if (item.children) {
              const childPath = buildMenuPath(targetItem, item.children, currentPath)
              if (childPath.length > 0) {
                return childPath
              }
            }
          }
          return []
        }
        
        const menuPath = buildMenuPath(menuItem, menuItems.value)
        
        // 添加首页
        if (menuPath.length > 0) {
          crumbs.push({ title: '首页', path: '/' })
        }
        
        // 构建菜单层级面包屑
        menuPath.forEach((item, index) => {
          crumbs.push({
            title: item.title,
            path: index === menuPath.length - 1 ? undefined : item.path
          })
        })
      } else {
        // 回退到基于路径的面包屑生成
        const pathSegments = route.path.split('/').filter(Boolean)
        
        // 添加首页
        if (pathSegments.length > 0) {
          crumbs.push({ title: '首页', path: '/' })
        }
        
        // 根据路径段查找对应的菜单项
        let currentPath = ''
        for (const segment of pathSegments) {
          currentPath += `/${segment}`
          const segmentMenuItem = findMenuByPath.value(currentPath)
          if (segmentMenuItem) {
            crumbs.push({ 
              title: segmentMenuItem.title, 
              path: currentPath === route.path ? undefined : currentPath 
            })
          } else {
            // 如果找不到对应菜单项，使用路径段作为标题
            const title = segment.charAt(0).toUpperCase() + segment.slice(1)
            crumbs.push({ 
              title, 
              path: currentPath === route.path ? undefined : currentPath 
            })
          }
        }
      }
    }
    
    breadcrumbs.value = crumbs
  }
  
  const updateBreadcrumbsById = (menuId: string) => {
    const menuItem = findMenuById.value(menuId)
    if (!menuItem) return
    
    const crumbs: { title: string; path?: string }[] = []
    
    // 递归构建面包屑路径
    const buildPath = (item: MenuItem, items: MenuItem[]): MenuItem[] => {
      for (const menuItem of items) {
        if (menuItem.id === item.id) {
          return [menuItem]
        }
        if (menuItem.children) {
          const path = buildPath(item, menuItem.children)
          if (path.length > 0) {
            return [menuItem, ...path]
          }
        }
      }
      return []
    }
    
    const path = buildPath(menuItem, menuItems.value)
    
    // 添加首页
    if (path.length > 0) {
      crumbs.push({ title: '首页', path: '/' })
    }
    
    // 构建面包屑
    path.forEach((item, index) => {
      crumbs.push({
        title: item.title,
        path: index === path.length - 1 ? undefined : item.path
      })
    })
    
    breadcrumbs.value = crumbs
  }
  
  const fetchMenuItems = async () => {
    isLoading.value = true
    try {
      // 这里应该从API获取菜单数据；暂以不同应用的模拟数据占位
      const path = typeof window !== 'undefined' ? window.location.pathname : ''
      const isMerchant = path.includes('/merchant-admin/')
      const isEnterprise = path.includes('/enterprise-admin/')

      const mockMenuItems: MenuItem[] = isEnterprise
        ? [
            { id: 'dashboard', title: '仪表盘', icon: 'House', path: '/dashboard' },
            { id: 'supply-menus', title: '供餐菜单', icon: 'Food', path: '/supply/menus' },
            {
              id: 'merchants-root',
              title: '我的商户',
              icon: 'Shop',
              children: [
                { id: 'restaurants', title: '供餐餐厅', path: '/merchants/restaurants' },
                { id: 'trial-requests', title: '试餐申请', path: '/merchants/trial-requests' },
                { id: 'restaurant-info', title: '餐厅信息', path: '/merchants/restaurant-info' },
                { id: 'blacklist', title: '黑名单', path: '/merchants/blacklist' }
              ]
            },
            { id: 'order-management', title: '订单管理', icon: 'Tickets', path: '/orders/center' },
            { id: 'employees', title: '员工管理', icon: 'User', path: '/employees' },
            {
              id: 'finance-root',
              title: '补贴与钱包',
              icon: 'Coin',
              children: [
                { id: 'recharge-management', title: '充值管理', path: '/finance/recharge-management' },
                { id: 'subsidies', title: '补贴管理', path: '/finance/subsidies' },
                { id: 'subsidy-batches', title: '补贴批次', path: '/finance/subsidy-batches' },
                { id: 'wallet-config', title: '钱包配置', path: '/finance/wallet-config' },
                { id: 'wallet', title: '钱包流水', path: '/finance/wallet' },
                { id: 'subsidy-transfer', title: '补贴转赠', path: '/finance/subsidy-transfer' }
              ]
            },
            { id: 'reconciliation', title: '财务对账', icon: 'Document', path: '/finance/reconciliation' },
            { id: 'analytics', title: '统计分析', icon: 'DataLine', path: '/analytics' },
            { id: 'calendar', title: '企业日历', icon: 'Calendar', path: '/calendar' }
          ]
        : isMerchant
        ? [
            { id: 'dashboard', title: '仪表盘', icon: 'House', path: '/dashboard' },
            { id: 'merchant-home', title: '商家主页', icon: 'UserFilled', path: '/merchant' },
            {
              id: 'schedules-root',
              title: '排期与菜单',
              icon: 'Calendar',
              children: [
                { id: 'schedules', title: '我的排期', path: '/schedules' },
                { id: 'dishes', title: '我的菜品', path: '/dishes' },
                // ✅ 说明：菜品分类/菜单编辑/标签管理已整合到页面 Tab（无需二级菜单占位入口）
              ]
            },
            {
              id: 'orders-root',
              title: '订单中心',
              icon: 'Tickets',
              children: [
                { id: 'order-center', title: '出单中心', path: '/orders/center' },
                { id: 'orders', title: '订单管理', path: '/orders' },
              ]
            },
            { id: 'suppliers', title: '供应商', icon: 'Goods', path: '/suppliers' },
            { id: 'finance', title: '财务中心', icon: 'Coin', path: '/finance' },
            { id: 'reports', title: '统计分析', icon: 'DataLine', path: '/reports' },
            { id: 'delivery', title: '配送计划', icon: 'Van', path: '/delivery' },
            { id: 'account', title: '账号管理', icon: 'Setting', path: '/account' }
          ]
        : [
            // operator-admin 默认菜单（原有占位）
            {
              id: 'dashboard',
              title: '仪表盘',
              icon: 'House',
              path: '/dashboard'
            },
            {
              id: 'system',
              title: '系统管理',
              icon: 'Setting',
              children: [
                { id: 'users', title: '用户管理', path: '/system/users' },
                { id: 'roles', title: '角色管理', path: '/system/roles' },
                { id: 'permissions', title: '权限管理', path: '/system/permissions' }
              ]
            },
            {
              id: 'business',
              title: '业务管理',
              icon: 'Shop',
              children: [
                { id: 'merchants', title: '商户管理', path: '/business/merchants' },
                { id: 'enterprises', title: '企业管理', path: '/business/enterprises' },
                { id: 'orders', title: '订单管理', path: '/business/orders' }
              ]
            }
          ]

      setMenuItems(mockMenuItems)
    } finally {
      isLoading.value = false
    }
  }
  
  const addMenuItem = (item: MenuItem) => {
    menuItems.value.push(item)
  }
  
  const removeMenuItem = (id: string) => {
    const removeFromItems = (items: MenuItem[]): MenuItem[] => {
      return items.filter(item => {
        if (item.id === id) return false
        if (item.children) {
          item.children = removeFromItems(item.children)
        }
        return true
      })
    }
    
    menuItems.value = removeFromItems(menuItems.value)
  }
  
  const updateMenuItem = (id: string, updates: Partial<MenuItem>) => {
    const updateInItems = (items: MenuItem[]) => {
      for (const item of items) {
        if (item.id === id) {
          Object.assign(item, updates)
          return
        }
        if (item.children) {
          updateInItems(item.children)
        }
      }
    }
    
    updateInItems(menuItems.value)
  }
  
  const clearMenu = () => {
    menuItems.value = []
    breadcrumbs.value = []
    activeMenuId.value = ''
  }
  
  // 最近访问与收藏（轻量本地持久化，按应用隔离）
  function getAppNamespace(): string {
    try {
      const p = window.location.pathname || ''
      if (p.includes('/merchant-admin/')) return 'merchant'
      if (p.includes('/operator-admin/')) return 'operator'
      if (p.includes('/enterprise-admin/')) return 'enterprise'
    } catch {}
    return 'app'
  }
  const NS = getAppNamespace()
  const RECENT_KEY = `${NS}:recentlyVisited`
  const FAVORITE_KEY = `${NS}:favorites`
  const MAX_RECENT = 12

  function readArray(key: string): string[] {
    try { return JSON.parse(localStorage.getItem(key) || '[]') as string[] } catch { return [] }
  }
  function writeArray(key: string, arr: string[]) { localStorage.setItem(key, JSON.stringify(arr)) }

  function recordRecentlyVisitedById(menuId: string) {
    const arr = readArray(RECENT_KEY)
    const idx = arr.indexOf(menuId)
    if (idx >= 0) arr.splice(idx, 1)
    arr.unshift(menuId)
    if (arr.length > MAX_RECENT) arr.length = MAX_RECENT
    writeArray(RECENT_KEY, arr)
  }

  function toggleFavorite(menuId: string): boolean {
    const arr = readArray(FAVORITE_KEY)
    const idx = arr.indexOf(menuId)
    let favored = false
    if (idx >= 0) {
      arr.splice(idx, 1)
      favored = false
    } else {
      arr.unshift(menuId)
      favored = true
    }
    writeArray(FAVORITE_KEY, arr)
    return favored
  }

  function getRecentMenus(): MenuItem[] {
    const ids = readArray(RECENT_KEY)
    return ids.map(id => findMenuById.value(id)).filter(Boolean) as MenuItem[]
  }

  function getFavoriteMenus(): MenuItem[] {
    const ids = readArray(FAVORITE_KEY)
    return ids.map(id => findMenuById.value(id)).filter(Boolean) as MenuItem[]
  }

  function isFavorite(menuId: string): boolean {
    if (!menuId) return false
    const ids = readArray(FAVORITE_KEY)
    return ids.includes(menuId)
  }
  
  return {
    // 状态
    menuItems,
    breadcrumbs,
    activeMenuId,
    isLoading,
    
    // 计算属性
    flatMenuItems,
    findMenuById,
    findMenuByPath,
    
    // 操作方法
    setMenuItems,
    setActiveMenu,
    updateBreadcrumbs,
    updateBreadcrumbsById,
    fetchMenuItems,
    addMenuItem,
    removeMenuItem,
    updateMenuItem,
    clearMenu,
    // 最近访问/收藏
    toggleFavorite,
    getRecentMenus,
    getFavoriteMenus,
    isFavorite
  }
})